import axios from 'axios';
import { getServerUrl } from '../config/runtimeConfig';

const API_BASE_URL = getServerUrl();

class AuthService {
  constructor() {
    this.api = axios.create({
      baseURL: `${API_BASE_URL}/api/auth`,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle token refresh
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const refreshToken = localStorage.getItem('refreshToken');
            if (refreshToken) {
              const response = await axios.post(
                `${API_BASE_URL}/api/auth/refresh-token`,
                { refreshToken }
              );

              const { token: newToken, refreshToken: newRefreshToken } = response.data;
              
              localStorage.setItem('token', newToken);
              localStorage.setItem('refreshToken', newRefreshToken);
              
              // Retry original request with new token
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return this.api(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed, redirect to login
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            window.location.href = '/login';
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Login user
  async login(credentials) {
    try {
      const response = await this.api.post('/login', credentials);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  }

  // Google OAuth login
  async googleLogin(googleToken) {
    try {
      const response = await this.api.post('/google-login', { 
        token: googleToken 
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Google login failed');
    }
  }

  // Register user
  async register(userData) {
    try {
      const response = await this.api.post('/register', userData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Registration failed');
    }
  }

  // Logout user
  async logout() {
    try {
      await this.api.post('/logout');
    } catch (error) {
      console.error('Logout error:', error);
    }
  }

  // Get user profile
  async getProfile() {
    try {
      const response = await this.api.get('/profile');
      return response.data.user;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to get profile');
    }
  }

  // Update user profile
  async updateProfile(userData) {
    try {
      const response = await this.api.put('/profile', userData);
      return response.data.user;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update profile');
    }
  }

  // Change password
  async changePassword(passwords) {
    try {
      const response = await this.api.put('/change-password', passwords);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to change password');
    }
  }

  // Admin: Get all users
  async getUsers(page = 1, limit = 10) {
    try {
      const response = await this.api.get(`/users?page=${page}&limit=${limit}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to get users');
    }
  }

  // Admin: Update user permission
  async updateUserPermission(userId, permissionLevel) {
    try {
      const response = await this.api.put(`/users/${userId}/permission`, {
        permissionLevel
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update user permission');
    }
  }

  // Admin: Update user status
  async updateUserStatus(userId, isActive) {
    try {
      const response = await this.api.put(`/users/${userId}/status`, {
        isActive
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update user status');
    }
  }

  // Admin: Update user profile (name and email)
  async updateUserProfile(userId, profileData) {
    try {
      const response = await this.api.put(`/users/${userId}/profile`, profileData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update user profile');
    }
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!localStorage.getItem('token');
  }

  // Get stored token
  getToken() {
    return localStorage.getItem('token');
  }

  // Get stored refresh token
  getRefreshToken() {
    return localStorage.getItem('refreshToken');
  }
}

const authService = new AuthService();
export default authService;
